/* eslint-disable @typescript-eslint/no-explicit-any */

import { get } from '../Services/common';
import { ObjectType } from '../Types/base';

export function deepClone<T>(obj: T) {
	if (obj === null || typeof obj !== 'object') {
		return obj;
	}

	if (obj instanceof Date) {
		return new Date(obj.getTime()) as any as T;
	}

	if (Array.isArray(obj)) {
		const arrCopy: any[] = [];
		for (const item of obj) {
			arrCopy.push(deepClone(item));
		}
		return arrCopy as T;
	}

	const objCopy = {} as T;
	for (const key in obj as any) {
		if (Object.prototype.hasOwnProperty.call(obj as any, key)) {
			(objCopy as any)[key] = deepClone((obj as any)[key]);
		}
	}
	return objCopy;
}

//a function that builds a breadcrumb name from an array of objects with the properties `Name`, `Id` and `ParentId`. the inputs are the array and the id of the current object
export function buildBreadcrumbName<T extends { Name: string; Id: number; ParentId?: number }>(arr: T[], id: number): string {
	const names: string[] = [];
	let currentId = id;

	while (currentId !== -1) {
		const item = arr.find((i) => i.Id === currentId);
		if (!item) break;
		names.unshift(item.Name);
		currentId = item.ParentId ?? -1;
	}

	return names.join(' > ');
}

export const getObjectTypeName = (type: ObjectType) => {
	switch (type) {
		case ObjectType.Event:
			return 'Event';
		case ObjectType.CMSPage:
			return 'CMS Page';
		case ObjectType.BlogPost:
			return 'Blog Post';
		case ObjectType.OnlineEducation:
			return 'Online Education';
		case ObjectType.GenericTag:
			return 'Generic Tag';
		case ObjectType.Certification:
			return 'Certification';
		case ObjectType.EngagementGoal:
			return 'Engagement Goal';
		case ObjectType.EngagementActivity:
			return 'Engagement Activity';
		case ObjectType.EngagementTodo:
			return 'Engagement To-Do';
		case ObjectType.MediabankFile:
			return 'Mediabank File';
		case ObjectType.User:
			return 'User';
		case ObjectType.Unit:
			return 'Unit';
		case ObjectType.FrontEndObject:
			return 'Front-End Object';
		case ObjectType.Document:
			return 'Document';
		case ObjectType.DocumentCategory:
			return 'Document Category';
		case ObjectType.DocumentCategoryFile:
			return 'Document Category File';
		case ObjectType.DocumentOriginalFile:
			return 'Document Original File';
		case ObjectType.DocumentContentFile:
			return 'Document Content File';
		case ObjectType.DocumentAttachedFile:
			return 'Document Attached File';
		case ObjectType.ProjectTask:
			return 'Project Task';
		default:
			return 'None';
	}
};

export async function fetchFileAndConvertToBase64DataUrl(url: string): Promise<string | null> {
	const blob = await get<Blob>(url);
	if (!blob) return null;

	const reader = new FileReader();
	const dataUrl = await new Promise((res) => {
		reader.onload = () => res(reader.result);
		reader.readAsDataURL(blob);
	});

	return dataUrl as string;
}

export const isNotNullish = <T>(v: T | null | undefined): v is T => v != null && v !== undefined;
export const isNullish = <T>(v: T | null | undefined): v is null | undefined => v == null || v === undefined;

export const getValueOrDefault = <T>(value: T | null | undefined, defaultValue: T): T => {
	return isNotNullish(value) ? value : defaultValue;
};
